import type {
  Artist,
  Theme,
  Release,
  ReleaseDetail,
  Link,
  PressItem,
  Redirect,
  StreamingLink,
  SignalResponse,
  HubUser,
  InviteUser,
  UpdateHubUser,
  SignalConfigDb,
  CreateSignalConfig,
  UpdateSignalConfig,
  SignalFeatured,
  CreateSignalFeaturedInput,
  ReorderSignalFeaturedInput,
  Album,
  CreateAlbum,
  UpdateAlbum,
  AlbumDeletionResult,
  TrackDeletionResult,
  ConsultantAlbum,
  ConsultantAlbumDetail,
  ConsultantAlbumAccess,
  ConsultantTrack,
  Track,
  TrackStatus,
  CreateTrack,
  UpdateTrack,
  TrackAsset,
  CreateTrackAsset,
  ComposerTurnRequest,
  ComposerTurnAccepted,
  ComposerTurnStatusResponse,
  MvScriptTurnRequest,
  MvScriptTurnResponse,
  MvScriptResponse,
  SaveMvScriptRequest,
  MvDeliveryResponse,
  MvDeliveryPackage,
  DeliverMvTargetRequest,
  SendShotToVeoRequest,
  SendShotToVeoResponse,
  PollShotRequest,
  PollShotResponse,
  IngestTakeRequest,
  IngestTakeResponse,
  IngestAssetRequest,
  IngestJob,
  IngestJobAccepted,
  ImageCandidateListQuery,
  ImageCandidateSummary,
  CharacterImageModel,
  CharacterListResponse,
  CharacterDetail,
  CreateCharacterRequest,
  UpdateCharacterRequest,
  GenerateCharacterSheetRequest,
  AddCharacterTileRequest,
  AlbumArcResponse,
  AlbumArcTurnRequest,
  AlbumArcTurnResponse,
  SaveAlbumArcRequest,
  CreateArcElementRequest,
  GenerateArcElementSheetRequest,
  ManagedImageSelection,
  ManagedImageSelectionQuery,
  SelectManagedImageRequest,
  AlbumImageSelectionCoverage,
  AlbumImageSelectionCoverageQuery,
  RenditionMatrix,
  RenditionMatrixQuery,
  RenditionExportRequest,
  RenditionExportResponse,
  AssetBrowseParams,
  AssetBrowseResponse,
  LibrarianTurnRequest,
  LibrarianTurnResponse,
  SaveImagePromptRequest,
  GenerateUploadUrl,
  RegisterAssetResponse,
  TranscriptionJob,
  TranscriptionJobStatusResponse,
  VoiceTranscribeResponse,
  ExportBundle,
  CreateSunoExportResponse,
  LyricsLineageGraphResponse,
  RollbackTrackAssetResponse,
  SaveIterationRequest,
  TrackArtifactsResponse,
  TrackCompareResponse,
  RetranscribeRequest,
  StructureLyricsRequest,
  StructureLyricsResponse,
  AlignLyricsRequest,
  AlignLyricsCreated,
  AlignLyricsSkipped,
  TrackDispositionRequest,
  TrackDispositionResponse,
  AnalyzeTrackRequest,
  AnalyzeTrackResponse,
  AlbumAssetSummaryResponse,
  PrePublishReport,
  PrePublishReportOptions,
  PublishRequest,
  PublishResponse,
  DspExportManifest,
  AssetLineageGraphResponse,
  DistributionMetadataV1,
  ReleaseRevision,
  DistributionMetadataValues,
  EffectiveDistributionMetadata,
  DistributionReadinessResponse,
  SaveOriginalLyricsRequest,
  SaveSunoStyleRequest,
  CatalogSearchParams,
  CatalogSearchResult,
  CatalogTrackSummary,
  CatalogTrackDetail,
  CreateCatalogTrackRequest,
  UpdateCatalogTrackRequest,
  TrackUsage,
  PlacementEvent,
  AlbumPlacement,
  AlbumPublication,
  UpsertAlbumPublication,
  AddPlacement,
  CatalogTrack,
  ReplacePlacement,
  ReplacePlacementResult,
  UpdatePlacementSelections,
  ReplaceMasterRequest,
  ReplaceMasterResponse,
  RederiveJob,
} from '@tc/shared'

import { getEnv } from '../config/env'

const API_BASE_URL = (getEnv('VITE_API_BASE_URL') || '/api').replace(/\/$/, '')

interface ArtistWithTheme extends Artist {
  theme?: Theme
}

interface ListResponse<T> {
  data: T[]
  meta?: {
    total: number
  }
}

interface TrackAssetUploadUrlResponse {
  uploadUrl: string
  blobUrl: string
  storagePath: string
  expiresAt: string
}

interface TrackAssetDuplicateResponse {
  duplicate: true
  asset: TrackAsset
}

type TrackAssetUploadUrlResult = TrackAssetUploadUrlResponse | TrackAssetDuplicateResponse

interface ReleaseWithLinks extends Release {
  streamingLinks?: StreamingLink[]
}

/**
 * The public `/health` response. The API only includes the `db` migration block
 * when it runs in CMS content mode (`apps/api/src/app.ts`), so the presence of
 * `db` is the canonical web-side signal for CMS vs static mode — see
 * {@link ApiClient.getHealth}.
 */
interface HealthResponse {
  status: string
  timestamp: string
  buildSha: string
  db?: {
    migrationsApplied: boolean
    lastMigration: string | null
    hasFailedMigrations: boolean
  }
}

/** The song that already holds a catalog slug (409 `CATALOG_SLUG_CONFLICT`). */
export interface ApiConflict {
  trackId: string
  title: string
  slug: string
}

export class ApiError extends Error {
  statusCode: number
  code: string
  correlationId?: string
  diagnosticsUrl?: string
  /** Set on a 409 whose body names the existing record (ADR-179 slug conflicts). */
  conflict?: ApiConflict

  constructor(message: string, statusCode: number, code: string, correlationId?: string, diagnosticsUrl?: string, conflict?: ApiConflict) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.code = code
    this.correlationId = correlationId
    this.diagnosticsUrl = diagnosticsUrl
    this.conflict = conflict
  }
}

/** Read the `conflict` object from an error body when it has the expected shape. */
function conflictOf(body: unknown): ApiConflict | undefined {
  const c = (body as { conflict?: Partial<ApiConflict> } | null)?.conflict
  if (!c || typeof c.trackId !== 'string' || typeof c.title !== 'string' || typeof c.slug !== 'string') return undefined
  return { trackId: c.trackId, title: c.title, slug: c.slug }
}

type GetTokenFn = () => Promise<string | null>

class ApiClient {
  private baseUrl: string
  private getToken: GetTokenFn | null = null

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  setTokenProvider(getToken: GetTokenFn): void {
    this.getToken = getToken
  }

  private async fetch<T>(path: string, options?: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
      ...options?.headers as Record<string, string>,
    }
    if (options?.body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      const message = body?.error?.message ?? body?.message ?? `Request failed: ${response.status}`
      const code = body?.error?.code ?? 'UNKNOWN'
      const correlationId = body?.error?.correlationId ?? response.headers.get('x-correlation-id') ?? undefined
      const diagnosticsUrl = body?.error?.diagnosticsUrl ?? undefined
      throw new ApiError(message, response.status, code, correlationId, diagnosticsUrl, conflictOf(body))
    }

    if (response.status === 204) {
      return null as T
    }

    return response.json()
  }

  private async authFetch<T>(path: string, options?: RequestInit): Promise<T> {
    if (!this.getToken) {
      throw new Error('Token provider not configured')
    }

    const token = await this.getToken()
    if (!token) {
      throw new Error('Not authenticated')
    }

    return this.fetch<T>(path, {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${token}`,
      },
    })
  }

  /**
   * Fetch the public `/health` document. The API adds the `db` migration block
   * only in CMS mode, so callers detect content mode by `db` presence (see
   * {@link HealthResponse}). Unauthenticated; safe to call before sign-in.
   *
   * @returns The health document (`db` present ⇒ CMS mode, absent ⇒ static mode).
   */
  async getHealth(): Promise<HealthResponse> {
    return this.fetch<HealthResponse>('/health')
  }

  // Artist (returns artist with embedded theme)
  async getArtist(): Promise<ArtistWithTheme | null> {
    try {
      return await this.fetch<ArtistWithTheme>('/artist')
    } catch {
      return null
    }
  }

  // Releases
  async getReleases(): Promise<Release[]> {
    const response = await this.fetch<ListResponse<Release>>('/releases')
    return response.data
  }

  async getReleaseBySlug(slug: string): Promise<ReleaseDetail | null> {
    try {
      return await this.fetch<ReleaseDetail>(`/releases/${slug}`)
    } catch {
      return null
    }
  }

  async getLatestRelease(): Promise<Release | null> {
    try {
      return await this.fetch<Release>('/releases/latest')
    } catch {
      return null
    }
  }

  // Links
  async getLinks(): Promise<Link[]> {
    const response = await this.fetch<ListResponse<Link>>('/links')
    return response.data
  }

  async getAllLinks(): Promise<Link[]> {
    const response = await this.fetch<ListResponse<Link>>('/links/all')
    return response.data
  }

  // Press
  async getPressItems(type?: string): Promise<PressItem[]> {
    const path = type ? `/press?type=${type}` : '/press'
    const response = await this.fetch<ListResponse<PressItem>>(path)
    return response.data
  }

  async getPressItemById(id: string): Promise<PressItem | null> {
    try {
      return await this.fetch<PressItem>(`/press/${id}`)
    } catch {
      return null
    }
  }

  // Redirects
  async getRedirects(): Promise<Redirect[]> {
    const response = await this.fetch<{ redirects: Redirect[] }>('/config/redirects')
    return response.redirects
  }

  // Signal (SPEC-034 / ADR-091 composed payload: config, featured set, rotation)
  async getSignal(): Promise<SignalResponse | null> {
    try {
      return await this.fetch<SignalResponse>('/signal')
    } catch {
      return null
    }
  }

  // ========== Admin API Methods ==========

  // Admin Artist
  async getAdminArtist(): Promise<ArtistWithTheme> {
    return this.authFetch<ArtistWithTheme>('/admin/artist')
  }

  async updateAdminArtist(data: Partial<Artist>): Promise<ArtistWithTheme> {
    return this.authFetch<ArtistWithTheme>('/admin/artist', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Admin Releases
  async getAdminReleases(includeHidden = true): Promise<ReleaseWithLinks[]> {
    const response = await this.authFetch<ListResponse<ReleaseWithLinks>>(
      `/admin/releases?includeHidden=${includeHidden}`
    )
    return response.data
  }

  async getAdminRelease(id: string): Promise<ReleaseWithLinks> {
    return this.authFetch<ReleaseWithLinks>(`/admin/releases/${id}`)
  }

  async createAdminRelease(data: Omit<Release, 'id' | 'createdAt' | 'updatedAt'> & {
    streamingLinks?: Omit<StreamingLink, 'id'>[]
  }): Promise<ReleaseWithLinks> {
    return this.authFetch<ReleaseWithLinks>('/admin/releases', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAdminRelease(id: string, data: Partial<Release> & {
    streamingLinks?: Omit<StreamingLink, 'id'>[]
  }): Promise<ReleaseWithLinks> {
    return this.authFetch<ReleaseWithLinks>(`/admin/releases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteAdminRelease(id: string): Promise<void> {
    await this.authFetch<null>(`/admin/releases/${id}`, {
      method: 'DELETE',
    })
  }


  // Admin Links
  async getAdminLinks(): Promise<Link[]> {
    const response = await this.authFetch<ListResponse<Link>>('/admin/links')
    return response.data
  }

  async createAdminLink(data: Omit<Link, 'id'>): Promise<Link> {
    return this.authFetch<Link>('/admin/links', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAdminLink(id: string, data: Partial<Link>): Promise<Link> {
    return this.authFetch<Link>(`/admin/links/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteAdminLink(id: string): Promise<void> {
    await this.authFetch<null>(`/admin/links/${id}`, {
      method: 'DELETE',
    })
  }

  // Admin Press
  async getAdminPressItems(): Promise<PressItem[]> {
    const response = await this.authFetch<ListResponse<PressItem>>('/admin/press')
    return response.data
  }

  async createAdminPressItem(data: Omit<PressItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<PressItem> {
    return this.authFetch<PressItem>('/admin/press', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAdminPressItem(id: string, data: Partial<PressItem>): Promise<PressItem> {
    return this.authFetch<PressItem>(`/admin/press/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteAdminPressItem(id: string): Promise<void> {
    await this.authFetch<null>(`/admin/press/${id}`, {
      method: 'DELETE',
    })
  }

  // Admin Users
  async getAdminUsers(): Promise<HubUser[]> {
    return this.authFetch<HubUser[]>('/admin/users')
  }

  async getAdminUser(id: string): Promise<HubUser> {
    return this.authFetch<HubUser>(`/admin/users/${id}`)
  }

  async inviteAdminUser(data: InviteUser): Promise<HubUser> {
    return this.authFetch<HubUser>('/admin/users/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAdminUserAccessLevel(id: string, data: UpdateHubUser): Promise<HubUser> {
    return this.authFetch<HubUser>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async resendAdminUserInvite(id: string): Promise<HubUser> {
    return this.authFetch<HubUser>(`/admin/users/${id}/resend-invite`, {
      method: 'POST',
    })
  }

  async deleteAdminUser(id: string): Promise<void> {
    await this.authFetch<null>(`/admin/users/${id}`, {
      method: 'DELETE',
    })
  }

  // Admin Signal
  async getAdminSignalConfig(): Promise<SignalConfigDb> {
    return this.authFetch<SignalConfigDb>('/admin/signal/config')
  }

  async createAdminSignalConfig(data: CreateSignalConfig): Promise<SignalConfigDb> {
    return this.authFetch<SignalConfigDb>('/admin/signal/config', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAdminSignalConfig(id: string, data: UpdateSignalConfig): Promise<SignalConfigDb> {
    return this.authFetch<SignalConfigDb>(`/admin/signal/config/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteAdminSignalConfig(id: string): Promise<void> {
    await this.authFetch<null>(`/admin/signal/config/${id}`, {
      method: 'DELETE',
    })
  }

  // Admin Signal Featured (SPEC-034 / ADR-091 homepage band)
  async getAdminSignalFeatured(): Promise<SignalFeatured[]> {
    const response = await this.authFetch<{ data: SignalFeatured[] }>('/admin/signal/featured')
    return response.data
  }

  async createAdminSignalFeatured(data: CreateSignalFeaturedInput): Promise<SignalFeatured> {
    return this.authFetch<SignalFeatured>('/admin/signal/featured', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async reorderAdminSignalFeatured(items: ReorderSignalFeaturedInput['items']): Promise<SignalFeatured[]> {
    const response = await this.authFetch<{ data: SignalFeatured[] }>('/admin/signal/featured/reorder', {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
    return response.data
  }

  async deleteAdminSignalFeatured(id: string): Promise<void> {
    await this.authFetch<null>(`/admin/signal/featured/${id}`, {
      method: 'DELETE',
    })
  }

  // Admin Albums
  async getAdminAlbums(): Promise<Album[]> {
    const response = await this.authFetch<{ data: Album[] }>('/admin/albums')
    return response.data
  }

  async getAdminAlbum(id: string): Promise<Album> {
    return this.authFetch<Album>(`/admin/albums/${id}`)
  }

  // Admin Tracks
  async getAdminTracksByAlbum(albumId: string): Promise<Track[]> {
    const response = await this.authFetch<{ data: Track[] }>(`/admin/tracks/by-album/${albumId}`)
    return response.data
  }

  async getAdminTrack(id: string): Promise<Track> {
    return this.authFetch<Track>(`/admin/tracks/${id}`)
  }

  /**
   * Fire one turn of the S1 image-prompt Conductor session (ADR-092, async per
   * ADR-120). A turn takes ~78–93s server-side, so this returns 202 with a
   * `turnId` immediately instead of holding the connection open through
   * Cloudflare. Poll {@link composerTurnStatus} with that id until terminal — the
   * full turn payload arrives in the `succeeded` status.
   *
   * @param body - The turn request (ids, session id, turn, render count, directives).
   * @returns The accepted turn `{ turnId, status: 'running' }`.
   */
  async composerTurn(body: ComposerTurnRequest): Promise<ComposerTurnAccepted> {
    return this.authFetch<ComposerTurnAccepted>('/admin/composer/turn', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Poll one composer turn's state by id (ADR-120). `running` while the engine
   * works (`result`/`error` null); `succeeded` carries the full
   * {@link ComposerTurnResponse} in `result`; `failed` carries `error`.
   *
   * @param turnId - The turn id from {@link composerTurn}.
   * @returns The turn's current status.
   */
  async composerTurnStatus(turnId: string): Promise<ComposerTurnStatusResponse> {
    return this.authFetch<ComposerTurnStatusResponse>(`/admin/composer/turn/${turnId}`)
  }

  /**
   * The latest composer turn for a track (ADR-120), for resume-on-reload — a page
   * that reloads mid-turn reattaches to the running turn instead of firing a fresh
   * one. Returns `null` when the track has no turns yet (the 404 is swallowed).
   *
   * @param trackId - The track id.
   * @returns The latest turn status, or `null` when none exists.
   */
  async composerLatestTurn(trackId: string): Promise<ComposerTurnStatusResponse | null> {
    try {
      return await this.authFetch<ComposerTurnStatusResponse>(
        `/admin/composer/turn?trackId=${encodeURIComponent(trackId)}`,
      )
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) return null
      throw err
    }
  }

  /**
   * Browse visual assets across the whole catalog (SPEC-025/SPEC-033 S9) — the
   * Library surface's backing read. Returns a page of the latest version of each
   * asset matching the scope-lens / type / source / search filters plus the rail
   * facet counts. CMS-only (501 in static mode).
   *
   * @param params - The browse filters + paging (all optional).
   * @returns The page of assets, the total match count, and the facet counts.
   */
  async browseAssets(params: AssetBrowseParams = {}): Promise<AssetBrowseResponse> {
    const qs = new URLSearchParams()
    if (params.scope) qs.set('scope', params.scope)
    if (params.types && params.types.length) qs.set('types', params.types.join(','))
    if (params.source) qs.set('source', params.source)
    if (params.search) qs.set('search', params.search)
    if (params.limit !== undefined) qs.set('limit', String(params.limit))
    if (params.offset !== undefined) qs.set('offset', String(params.offset))
    const query = qs.toString()
    return this.authFetch<AssetBrowseResponse>(`/admin/track-assets/browse${query ? `?${query}` : ''}`)
  }

  /**
   * Run one S9 Library Conductor turn (ADR-092) — a deterministic, read-only
   * librarian action (find-similar / trace-lineage / summarize) over the visual
   * asset catalog. CMS-only (501 in static mode).
   *
   * @param body - The librarian turn request (action + optional focus asset + filters).
   * @returns The librarian turn result (plan, steps, findings, outcome, summary).
   */
  async librarianTurn(body: LibrarianTurnRequest): Promise<LibrarianTurnResponse> {
    return this.authFetch<LibrarianTurnResponse>('/admin/library/turn', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Persist an approved prompt draft as a versioned `image_prompt` asset
   * (SPEC-033) with generator provenance. CMS-only (501 in static mode).
   *
   * @param body - The save request (album/track ids, content, sourceTool, provenance).
   * @returns The created {@link TrackAsset}.
   */
  async saveImagePrompt(body: SaveImagePromptRequest): Promise<TrackAsset> {
    return this.authFetch<TrackAsset>('/admin/track-assets/save-image-prompt', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  // ─── S5 Characters (ADR-093) ───────────────────────────────────────────────

  /**
   * List the artist's character / reference library (S5). Works in both modes
   * (static → empty). Each card carries its lifecycle state, tile count, and
   * "Used in N" count.
   * @returns The library grid.
   */
  async listCharacters(): Promise<CharacterListResponse> {
    return this.authFetch<CharacterListResponse>('/admin/characters')
  }

  /**
   * Get one character in full — its sheet tiles, descriptor, fixed seed, and usage.
   * @param id - The character id.
   * @returns The character detail + usage rollup.
   */
  async getCharacter(id: string): Promise<CharacterDetail> {
    return this.authFetch<CharacterDetail>(`/admin/characters/${id}`)
  }

  /**
   * Create a draft-brief character (descriptor/seed derived when omitted). CMS-only.
   * @param body - Name, role scope, and optional brief/descriptor/seed asset ids.
   * @returns The created character + (empty) usage.
   */
  async createCharacter(body: CreateCharacterRequest): Promise<CharacterDetail> {
    return this.authFetch<CharacterDetail>('/admin/characters', { method: 'POST', body: JSON.stringify(body) })
  }

  /**
   * Update a character's identity block (name / role / brief / descriptor). CMS-only.
   * @param id - The character id.
   * @param body - The fields to change.
   * @returns The updated character + usage.
   */
  async updateCharacter(id: string, body: UpdateCharacterRequest): Promise<CharacterDetail> {
    return this.authFetch<CharacterDetail>(`/admin/characters/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
  }

  /**
   * Kick character-sheet generation (turnaround + expression set) and return the
   * 202 generating snapshot — `lifecycleState: 'generating_turnaround'`, tiles
   * cleared. Tiles stream in as the background processor advances; poll
   * {@link getCharacter} until a terminal state (`ready` / `errored`). When the
   * image model is unconfigured the character ends `errored` (retry), never a 500.
   * A 409 GENERATION_IN_PROGRESS means a run is already in flight. CMS-only.
   * @param id - The character id.
   * @param body - Optional expression-slot override + render-model.
   * @returns The generating (or, on re-fetch, ready/errored) character + usage.
   */
  async generateCharacterSheet(id: string, body: GenerateCharacterSheetRequest = {}): Promise<CharacterDetail> {
    return this.authFetch<CharacterDetail>(`/admin/characters/${id}/generate`, { method: 'POST', body: JSON.stringify(body) })
  }

  /**
   * Regenerate one sheet tile in place (same angle/expression). CMS-only. The
   * optional render-model override + per-run style note ride the querystring — the
   * POST stays bodyless.
   * @param id - The character id.
   * @param tileId - The tile's asset id.
   * @param model - Optional approved render model (default: server-configured).
   * @param styleNote - Optional per-run wardrobe/style directive (SPEC-044), applied to this render only.
   * @returns The updated character + usage.
   */
  async regenerateCharacterTile(id: string, tileId: string, model?: CharacterImageModel, styleNote?: string): Promise<CharacterDetail> {
    const params = new URLSearchParams()
    if (model) params.set('model', model)
    if (styleNote) params.set('styleNote', styleNote)
    const query = params.toString() ? `?${params.toString()}` : ''
    return this.authFetch<CharacterDetail>(`/admin/characters/${id}/tiles/${tileId}/regenerate${query}`, { method: 'POST' })
  }

  /**
   * Add one angle/expression tile to an existing sheet. CMS-only.
   * @param id - The character id.
   * @param body - The tile kind + slot.
   * @returns The updated character + usage.
   */
  async addCharacterTile(id: string, body: AddCharacterTileRequest): Promise<CharacterDetail> {
    return this.authFetch<CharacterDetail>(`/admin/characters/${id}/tiles`, { method: 'POST', body: JSON.stringify(body) })
  }

  /**
   * Run one turn of the S6 music-video Script Conductor (ADR-094, video-director
   * role). Resolves the track context server-side (titles, tags, locked
   * direction, parsed song sections) and returns the agent plan, session-log
   * timeline, and a deterministic draft scene list. Works in every content mode —
   * no external generation.
   *
   * @param body - The turn request (ids, session id, turn, target scenes, directives, toggles).
   * @returns The drafted turn result.
   */
  async mvScriptTurn(body: MvScriptTurnRequest): Promise<MvScriptTurnResponse> {
    return this.authFetch<MvScriptTurnResponse>('/admin/mv-script/turn', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Get the current music-video script for a track (ADR-094 S6) — its latest
   * `mv_script` asset (ordered scenes) plus the available song sections for
   * lyric-sync. Returns an empty script + sections in static mode.
   *
   * @param trackId - The track whose script to fetch.
   * @returns The current script response.
   */
  async getMvScript(trackId: string): Promise<MvScriptResponse> {
    return this.authFetch<MvScriptResponse>(`/admin/mv-script/by-track/${trackId}`)
  }

  /**
   * Persist the authored music-video scene list as a new versioned `mv_script`
   * asset (ADR-094 S6). CMS-only (501 in static mode).
   *
   * @param body - The save request (album/track ids + the scene list).
   * @returns The created {@link TrackAsset}.
   */
  async saveMvScript(body: SaveMvScriptRequest): Promise<TrackAsset> {
    return this.authFetch<TrackAsset>('/admin/mv-script/save', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Get the whole Album Arc for an album (ADR-094 S11) — the candidate arcs, the active
   * candidate, the arc-element library, the referenced characters, and the tracklist.
   * Works in both content modes (static → empty arc).
   *
   * @param albumId - The album whose arc to fetch.
   * @returns The {@link AlbumArcResponse}.
   */
  async getAlbumArc(albumId: string): Promise<AlbumArcResponse> {
    return this.authFetch<AlbumArcResponse>(`/admin/album-arc/by-album/${albumId}`)
  }

  /**
   * Run one album-director Conductor turn (ADR-094 S11) — drafts a candidate arc (vision
   * spine + sequenced chapters + proposed transitions) from every track. Deterministic and
   * cost-free; works in every content mode.
   *
   * @param body - The turn request (album id, session id, turn, directives).
   * @returns The {@link AlbumArcTurnResponse}.
   */
  async albumArcTurn(body: AlbumArcTurnRequest): Promise<AlbumArcTurnResponse> {
    return this.authFetch<AlbumArcTurnResponse>('/admin/album-arc/turn', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Persist the whole arc doc as a new versioned `album_arc` asset (ADR-094 S11). CMS-only
   * (501 in static mode). Selecting/committing a candidate is a save with a different
   * `activeCandidateId`.
   *
   * @param body - The save request (elements + candidates + character refs + active id).
   * @returns The created {@link TrackAsset}.
   */
  async saveAlbumArc(body: SaveAlbumArcRequest): Promise<TrackAsset> {
    return this.authFetch<TrackAsset>('/admin/album-arc/save', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Create a draft arc element (ADR-094 S11) and persist a new arc version. CMS-only.
   *
   * @param body - The create request (album id, role, name, optional brief/descriptor).
   * @returns The refreshed {@link AlbumArcResponse}.
   */
  async createArcElement(body: CreateArcElementRequest): Promise<AlbumArcResponse> {
    return this.authFetch<AlbumArcResponse>(`/admin/album-arc/${body.albumId}/elements`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Generate an arc element's reference sheet (ADR-094 S11). When the image model is
   * unconfigured the element returns in the `errored` + retry state — never a 500. CMS-only.
   *
   * @param albumId - The album owning the element.
   * @param elementId - The element to generate.
   * @param body - Optional slot override.
   * @returns The refreshed {@link AlbumArcResponse}.
   */
  async generateArcElement(albumId: string, elementId: string, body: GenerateArcElementSheetRequest = {}): Promise<AlbumArcResponse> {
    return this.authFetch<AlbumArcResponse>(`/admin/album-arc/${albumId}/elements/${elementId}/generate`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Get the current music-video delivery package for a track (ADR-094 S10) — its
   * latest `mv_delivery_package` asset (assembled cut + per-target delivery state),
   * or a null package when none is staged or in static mode.
   *
   * @param trackId - The track whose delivery package to fetch.
   * @returns The current delivery response.
   */
  async getMvDelivery(trackId: string): Promise<MvDeliveryResponse> {
    return this.authFetch<MvDeliveryResponse>(`/admin/mv-delivery/by-track/${trackId}`)
  }

  /**
   * Deliver one target of an assembled music-video delivery package (ADR-094 S10).
   * Dispatches to a webhook / internal target, records the attempt + status,
   * persists a new `mv_delivery_package` version, and returns the updated package.
   * CMS-only (501 in static mode); the `roundtrip` target is rejected (400).
   *
   * @param body - The deliver request (album/track ids, target, optional caption, package).
   * @returns The updated delivery package.
   */
  async deliverMvTarget(body: DeliverMvTargetRequest): Promise<MvDeliveryPackage> {
    return this.authFetch<MvDeliveryPackage>('/admin/mv-delivery/deliver', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Hand a saved music-video shot to Veo (ADR-094 §9 Scene Detail). The route
   * derives the recipe op (world text-to-video vs protagonist reference-to-video)
   * from the shot's reference assets and submits through the vertex-client.
   * Generation never throws for an unconfigured/failed submit: it is reported as a
   * `serviceError` inside the response (the designed error state), with the manual
   * copy-and-import path staying available. Paid + CMS-only (501 in static mode);
   * a re-submitted `(sessionId, turn)` 409s, past the per-session cap 429s.
   *
   * @param body - The send request (ids, session/turn, shot id, saved prompt, reference refs).
   * @returns The recipe op plus an operation name or a service error.
   */
  async sendShotToVeo(body: SendShotToVeoRequest): Promise<SendShotToVeoResponse> {
    return this.authFetch<SendShotToVeoResponse>('/admin/mv-scene/send-shot', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Poll a submitted Veo operation for completion (ADR-094 §9). While `done` is
   * false the caller keeps polling; once `done`, `videos` carries the GCS render(s)
   * to ingest. An unconfigured/failed poll is reported as a `serviceError` in the 200.
   *
   * @param body - The poll request (the operation name from {@link sendShotToVeo}).
   * @returns The operation state (done, output videos, and any service/op error).
   */
  async pollShot(body: PollShotRequest): Promise<PollShotResponse> {
    return this.authFetch<PollShotResponse>('/admin/mv-scene/poll-shot', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Ingest a completed Veo take as a `video_clip` track-asset (ADR-094 §8/§9).
   * Supply either an `operationName` (polled server-side) or a direct `gcsUri`; the
   * server downloads the clip and registers it. CMS-only (501 in static mode).
   *
   * @param body - The ingest request (trackId + operationName|gcsUri + optional shot/take meta).
   * @returns The created asset summary, or a `serviceError` inside the 200.
   */
  async ingestTake(body: IngestTakeRequest): Promise<IngestTakeResponse> {
    return this.authFetch<IngestTakeResponse>('/admin/mv-scene/ingest-take', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Server-side ingest of a returned clip from a reachable https URL (the Import
   * take paste-URL path). Streams the URL to blob storage, computes the sha256, and
   * registers the asset in the background — returns a job id to poll with
   * {@link getIngestJob}. CMS-only (501 in static mode).
   *
   * @param body - The ingest request (trackId, type, sourceUrl, optional filename/notes/source).
   * @returns The accepted ingest job id.
   */
  async ingestTrackAsset(body: IngestAssetRequest): Promise<IngestJobAccepted> {
    return this.authFetch<IngestJobAccepted>('/admin/track-assets/ingest', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Poll an ingest job (the Import take paste-URL path). On `completed` the job's
   * `resultAssetId` is the registered clip asset id.
   *
   * @param jobId - The ingest job id from {@link ingestTrackAsset}.
   * @returns The current ingest job record.
   */
  async getIngestJob(jobId: string): Promise<IngestJob> {
    return this.authFetch<IngestJob>(`/admin/track-assets/ingest-jobs/${jobId}`)
  }

  /**
   * List an album's release revisions, newest first.
   *
   * @param albumId - The album id.
   * @returns The album's revision history; empty when it has never had one.
   */
  async getAlbumRevisions(albumId: string): Promise<ReleaseRevision[]> {
    const res = await this.authFetch<{ data: ReleaseRevision[] }>(`/admin/albums/${albumId}/revisions`)
    return res.data
  }

  async getAlbumDistributionMetadata(albumId: string): Promise<DistributionMetadataV1> {
    return this.authFetch<DistributionMetadataV1>(`/admin/albums/${albumId}/distribution-metadata`)
  }

  async setAlbumDistributionMetadata(
    albumId: string,
    values: DistributionMetadataValues,
  ): Promise<DistributionMetadataV1> {
    return this.authFetch<DistributionMetadataV1>(`/admin/albums/${albumId}/distribution-metadata`, {
      method: 'PUT',
      body: JSON.stringify(values),
    })
  }

  async getTrackDistributionMetadata(trackId: string): Promise<EffectiveDistributionMetadata> {
    return this.authFetch<EffectiveDistributionMetadata>(`/admin/tracks/${trackId}/distribution-metadata`)
  }

  async setTrackDistributionMetadata(
    trackId: string,
    values: DistributionMetadataValues,
  ): Promise<EffectiveDistributionMetadata> {
    return this.authFetch<EffectiveDistributionMetadata>(`/admin/tracks/${trackId}/distribution-metadata`, {
      method: 'PUT',
      body: JSON.stringify(values),
    })
  }

  async getAlbumDistributionReadiness(albumId: string): Promise<DistributionReadinessResponse> {
    return this.authFetch<DistributionReadinessResponse>(`/admin/albums/${albumId}/distribution-metadata/readiness?target=amuse`)
  }

  // Admin Albums CRUD
  async createAdminAlbum(data: CreateAlbum): Promise<Album> {
    return this.authFetch<Album>('/admin/albums', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAdminAlbum(id: string, data: UpdateAlbum): Promise<Album> {
    return this.authFetch<Album>(`/admin/albums/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete an album (TASK-1198). Pass `{ cascade: true }` to also delete the
   * album's tracks and assets (reference-counted blob GC) and detach any linked
   * release; without it the API returns 409 `CASCADE_BLOCK` when children exist.
   * `{ dryRun: true }` returns the projected summary without deleting anything;
   * its `blockedByRevisions` is non-empty when an ADR-122 release revision pins
   * something in the album, meaning the real call would be refused with 409
   * `REVISION_PIN_BLOCK` — a hard block no `cascade` overrides.
   *
   * @param id - Album UUID.
   * @param options - `cascade`, `dryRun`, and an optional audit `reason`.
   * @returns The deletion summary (counts of removed tracks/assets and blob GC).
   */
  async deleteAdminAlbum(
    id: string,
    options: { cascade?: boolean; dryRun?: boolean; reason?: string } = {},
  ): Promise<AlbumDeletionResult> {
    const params = new URLSearchParams()
    if (options.cascade) params.set('cascade', 'true')
    if (options.dryRun) params.set('dryRun', 'true')
    if (options.reason) params.set('reason', options.reason)
    const query = params.toString()
    return this.authFetch<AlbumDeletionResult>(`/admin/albums/${id}${query ? `?${query}` : ''}`, {
      method: 'DELETE',
    })
  }

  // Admin Tracks CRUD
  async createAdminTrack(data: CreateTrack): Promise<Track> {
    return this.authFetch<Track>('/admin/tracks', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAdminTrack(id: string, data: UpdateTrack): Promise<Track> {
    return this.authFetch<Track>(`/admin/tracks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * Transition a track along the production status chain
   * (draft → mixed → mastered → published) via the dedicated
   * `POST /admin/tracks/:id/status` endpoint, which enforces the shared
   * `TRACK_STATUS_TRANSITIONS` legality server-side. Backward moves are never
   * allowed; `allowSkip` permits a forward skip past intermediate statuses for
   * legacy tracks.
   *
   * @param id - Track UUID.
   * @param targetStatus - The production status to transition to.
   * @param allowSkip - When true, permit a forward skip (e.g. draft → mastered).
   * @returns The updated track.
   * @throws ApiError 400 `ILLEGAL_STATUS_TRANSITION` on an illegal move, 501
   *   `CMS_ONLY` in static mode.
   */
  async transitionTrackStatus(id: string, targetStatus: TrackStatus, allowSkip = false): Promise<Track> {
    return this.authFetch<Track>(`/admin/tracks/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ targetStatus, allowSkip }),
    })
  }

  /**
   * Delete a track (TASK-1198). Pass `{ cascade: true }` to also delete the
   * track's assets (reference-counted blob GC); without it the API returns 409
   * `CASCADE_BLOCK` when assets exist. 409 `REVISION_PIN_BLOCK` is separate: an
   * ADR-122 release revision pins the track or an asset, blocking the delete
   * with or without `cascade`.
   *
   * @param id - Track UUID.
   * @param options - `cascade` and an optional audit `reason`.
   * @returns The deletion summary (count of removed assets and blob GC).
   */
  async deleteAdminTrack(
    id: string,
    options: { cascade?: boolean; reason?: string } = {},
  ): Promise<TrackDeletionResult> {
    const params = new URLSearchParams()
    if (options.cascade) params.set('cascade', 'true')
    if (options.reason) params.set('reason', options.reason)
    const query = params.toString()
    return this.authFetch<TrackDeletionResult>(`/admin/tracks/${id}${query ? `?${query}` : ''}`, {
      method: 'DELETE',
    })
  }

  // Admin Track Assets
  async getAdminTrackAssets(trackId: string): Promise<TrackAsset[]> {
    const response = await this.authFetch<{ data: TrackAsset[] }>(`/admin/track-assets/by-track/${trackId}`)
    return response.data
  }

  async generateTrackAssetUploadUrl(data: GenerateUploadUrl): Promise<TrackAssetUploadUrlResult> {
    return this.authFetch<TrackAssetUploadUrlResult>('/admin/track-assets/upload-url', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Register a track asset after a successful blob PUT. Contract v1.7 returns ONE
   * discriminated `{ outcome, ... }` shape; the UI only reaches this after a
   * non-duplicate presign + PUT, so it expects `created` (or `deduped` on a race)
   * and unwraps `.asset`. A `needsUpload` outcome means the blob PUT silently
   * failed — surfaced as an error so the caller retries rather than silently
   * "completing" with no asset.
   *
   * @param data - The asset-create fields.
   * @returns The registered (or deduped) {@link TrackAsset}.
   */
  async registerTrackAsset(data: CreateTrackAsset): Promise<TrackAsset> {
    const result = await this.authFetch<RegisterAssetResponse>('/admin/track-assets', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (result.outcome === 'needsUpload') {
      throw new ApiError('Upload did not complete; the blob is missing — please retry.', 409, 'NEEDS_UPLOAD')
    }
    return result.asset
  }

  async deleteTrackAsset(id: string): Promise<void> {
    await this.authFetch<null>(`/admin/track-assets/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Fetch a single track asset by id (`GET /admin/track-assets/:id`). Used by the
   * S9 Library to resolve a librarian finding that points at an asset outside the
   * current page.
   *
   * @param id - Track-asset UUID.
   * @returns The {@link TrackAsset}.
   */
  async getTrackAsset(id: string): Promise<TrackAsset> {
    return this.authFetch<TrackAsset>(`/admin/track-assets/${id}`)
  }

  async getAssetPreviewUrl(id: string): Promise<{ previewUrl: string; expiresAt: string; contentType: string }> {
    return this.authFetch<{ previewUrl: string; expiresAt: string; contentType: string }>(`/admin/track-assets/${id}/preview-url`)
  }

  /**
   * List the S2 image candidates (SPEC-033) for an album/track scope as
   * grid summaries. Filters by `purpose` (role) and `state` (default `ready`).
   * Returns `[]` in static mode (a plain read; never 501).
   *
   * @param query - Scope (albumId, optional trackId) and optional purpose/state filters.
   * @returns The candidate summaries, newest first.
   */
  async listImageCandidates(query: ImageCandidateListQuery): Promise<ImageCandidateSummary[]> {
    const params = new URLSearchParams({ albumId: query.albumId })
    if (query.trackId) params.set('trackId', query.trackId)
    if (query.purpose) params.set('purpose', query.purpose)
    if (query.state) params.set('state', query.state)
    const response = await this.authFetch<{ data: ImageCandidateSummary[] }>(`/admin/track-assets/image-candidates?${params.toString()}`)
    return response.data
  }

  /**
   * S3 Discard (SPEC-033): archive an image_candidate so it drops out of the
   * ready grid, deactivating any pinned selection referencing it. Idempotent;
   * the row + blob survive. CMS-only (the route 501s in static mode).
   *
   * @param assetId - The image_candidate asset id to archive.
   * @returns `{ ok, assetId }` on success.
   */
  async archiveImageCandidate(assetId: string): Promise<{ ok: boolean; assetId: string }> {
    return this.authFetch<{ ok: boolean; assetId: string }>(`/admin/track-assets/image-candidates/${assetId}/archive`, {
      method: 'POST',
    })
  }

  /**
   * S3 pinned selection (SPEC-035): read the current pinned image master for an
   * album/track scope + purpose. Returns `null` when nothing is pinned or in
   * static mode (a plain read; never 501).
   *
   * @param query - Scope (albumId, optional trackId) + purpose.
   * @returns The pinned {@link ManagedImageSelection}, or null.
   */
  async getMediaSelection(query: ManagedImageSelectionQuery): Promise<ManagedImageSelection | null> {
    const params = new URLSearchParams({ albumId: query.albumId, purpose: query.purpose })
    if (query.trackId) params.set('trackId', query.trackId)
    return this.authFetch<ManagedImageSelection | null>(`/admin/media/selection?${params.toString()}`)
  }

  /**
   * S3 Set-as-cover (SPEC-035): pin a non-stale image_candidate as the master for
   * an album/track scope + purpose. Supersedes any prior active selection.
   * CMS-only (the route 501s in static mode).
   *
   * @param body - The scope, purpose, and candidate asset id to pin.
   * @returns The new {@link ManagedImageSelection}.
   */
  async selectMediaImage(body: SelectManagedImageRequest): Promise<ManagedImageSelection> {
    return this.authFetch<ManagedImageSelection>('/admin/media/selection', {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  /**
   * S3 readiness (SPEC-035): per-track pinned-selection coverage across an album
   * for one purpose — the data behind the Visual Studio readiness meter + album
   * cover-status strip. Computed by one grouped query server-side (no N+1).
   * Returns `null` in static mode (a plain read; never 501).
   *
   * @param query - The album id + purpose.
   * @returns The {@link AlbumImageSelectionCoverage}, or null in static mode.
   */
  async getMediaSelectionCoverage(query: AlbumImageSelectionCoverageQuery): Promise<AlbumImageSelectionCoverage | null> {
    const params = new URLSearchParams({ albumId: query.albumId, purpose: query.purpose })
    return this.authFetch<AlbumImageSelectionCoverage | null>(`/admin/media/selection-coverage?${params.toString()}`)
  }

  /**
   * S4 (SPEC-025): the per-destination rendition format matrix for one track
   * cover — each social preset's target size/ratio + status (ready / needs_crop /
   * missing) against the approved cover, plus readiness counts and stored crops.
   * A plain read; in static mode `source` is null and every entry is `missing`.
   *
   * @param query - The album id + track id.
   * @returns The {@link RenditionMatrix}.
   */
  async getRenditionMatrix(query: RenditionMatrixQuery): Promise<RenditionMatrix> {
    const params = new URLSearchParams({ albumId: query.albumId, trackId: query.trackId })
    return this.authFetch<RenditionMatrix>(`/admin/media/renditions?${params.toString()}`)
  }

  /**
   * S4 (SPEC-025): produce (or SHA-dedup) a per-destination rendition of the
   * approved cover — crop + scale via ffmpeg, persisted as a derived_from
   * image_rendition child. Returns the asset + a short-lived download URL.
   * CMS-only (throws a 501 ApiError in static mode).
   *
   * @param body - The destination, crop rect, and fit mode.
   * @returns The {@link RenditionExportResponse}.
   */
  async exportRendition(body: RenditionExportRequest): Promise<RenditionExportResponse> {
    return this.authFetch<RenditionExportResponse>('/admin/media/renditions/export', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async getLyricsAssets(trackId: string): Promise<TrackAsset[]> {
    const response = await this.authFetch<{ data: TrackAsset[] }>(`/admin/track-assets/by-track/${trackId}/lyrics`)
    return response.data
  }

  async getLyricsLineage(trackId: string): Promise<LyricsLineageGraphResponse> {
    return this.authFetch<LyricsLineageGraphResponse>(`/admin/track-assets/by-track/${trackId}/lineage`)
  }

  /**
   * Read a single asset's bounded provenance graph (SPEC-023 P5): its
   * `ancestors` (what it was derived from) and `descendants` (what was derived
   * from it), each a flat edge set walked up to `depth` hops. `depth` defaults
   * server-side to 5 and is clamped to 10. Mirrors `@tc/hub-client`
   * `trackAssets.lineage`. CMS-only server-side (the route returns 501 in static
   * mode). Distinct from {@link getLyricsLineage}, which reads a track's lyrics
   * lineage by track id.
   *
   * @param assetId - Track-asset UUID.
   * @param options - Optional traversal depth (server clamps to 1..10).
   * @returns The asset's lineage graph (ancestors + descendants).
   */
  async getTrackAssetLineage(
    assetId: string,
    options?: { depth?: number }
  ): Promise<AssetLineageGraphResponse> {
    const query = options?.depth !== undefined ? `?depth=${options.depth}` : ''
    return this.authFetch<AssetLineageGraphResponse>(`/admin/track-assets/${assetId}/lineage${query}`)
  }

  /**
   * Idempotent upsert of a Generation Package (SunoExport). Returns the same
   * export when called with identical assembled content (SHA-256 idempotency).
   * SPEC-017 T021 threads optional provenance so the package records the exact
   * source lyric + sound-direction versions it was assembled from.
   *
   * @param trackId - Track UUID.
   * @param assembledContent - sunoStyle + "\n\n" + lyricsContent.
   * @param provenance - Optional source-version + exclude-content provenance.
   * @returns The export record with `exportId`, `isNew`, and the stored `export`.
   */
  async exportSuno(
    trackId: string,
    assembledContent: string,
    provenance?: {
      excludeContent?: string
      sourceLyricsAssetId?: string
      sourceLyricsVersion?: number
      sourceStyleAssetId?: string
      sourceStyleVersion?: number
    },
  ): Promise<CreateSunoExportResponse> {
    return this.authFetch<CreateSunoExportResponse>('/admin/track-assets/export-suno', {
      method: 'POST',
      body: JSON.stringify({ trackId, assembledContent, ...provenance }),
    })
  }

  /**
   * Fetches a SunoExport with its derived stem TrackAssets for the
   * /admin/exports/:id deep-link page.
   *
   * @param id - SunoExport UUID
   * @returns ExportBundle with full assembled content and stem assets
   */
  async getAdminExportDetail(id: string): Promise<ExportBundle> {
    return this.authFetch<ExportBundle>(`/admin/exports/${id}`)
  }

  /**
   * Album pre-publish asset-readiness summary — the per-track presence matrix
   * (audio / transcript / lyrics / cover art) plus an album-level progress
   * rollup. Mirrors `@tc/hub-client` `albums.assetSummary`. CMS-only server-side
   * (the route returns 501 in static mode).
   *
   * @param albumId - Album UUID.
   * @returns The album's asset summary (progress rollup + per-track status).
   */
  async getAlbumAssetSummary(albumId: string): Promise<AlbumAssetSummaryResponse> {
    return this.authFetch<AlbumAssetSummaryResponse>(`/admin/albums/${albumId}/asset-summary`)
  }

  /**
   * Compute an album's pre-publish readiness report WITHOUT transitioning it
   * (SPEC-016 / SPEC-022 R2). Mirrors `@tc/hub-client` `albums.prePublishReport`.
   * `options.targetStatus` defaults server-side to the album's natural next
   * status; `options.allowSkip` relaxes transition legality for a legacy album.
   * CMS-only server-side (the route returns 501 in static mode).
   *
   * @param albumId - Album UUID.
   * @param options - Optional target status and allow-skip query parameters.
   * @returns The pre-publish report (per-track checks, album entry, overall verdict).
   */
  async getAlbumPrePublishReport(
    albumId: string,
    options?: PrePublishReportOptions
  ): Promise<PrePublishReport> {
    const params = new URLSearchParams()
    if (options?.targetStatus) {
      params.set('targetStatus', options.targetStatus)
    }
    if (options?.allowSkip !== undefined) {
      params.set('allowSkip', String(options.allowSkip))
    }
    const query = params.toString()
    const path = `/admin/albums/${albumId}/pre-publish-report${query ? `?${query}` : ''}`
    return this.authFetch<PrePublishReport>(path)
  }

  /**
   * Validate the album against its pre-publish checks and, on a passing
   * non-dry-run request, advance its status (SPEC-016 / SPEC-022 R2). A
   * `body.dryRun` request returns the report without transitioning. On a failing
   * check the route responds 409 `PRE_PUBLISH_FAILED` (surfaced as an
   * {@link ApiError}). Mirrors `@tc/hub-client` `albums.publish`.
   *
   * @param albumId - Album UUID.
   * @param body - Target status, optional allow-skip, and optional dry-run flag.
   * @returns The publish outcome (previous + new status and the full report).
   */
  async publishAlbum(albumId: string, body: PublishRequest): Promise<PublishResponse> {
    return this.authFetch<PublishResponse>(`/admin/albums/${albumId}/publish`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /**
   * Assemble the album's single-target DSP distribution package (SPEC-022 R4):
   * the {@link DspExportManifest} — album metadata plus tracks ordered by
   * position, each with a required ISRC and a short-lived read-SAS `audioUrl`,
   * plus a read-SAS `coverArtUrl` and `generatedAt`. The route gates on the R2
   * asset checks: an album that is not release-ready responds 409
   * `PRE_PUBLISH_FAILED` and static mode responds 501 `CMS_ONLY` — both surface
   * as an {@link ApiError} the caller can branch on by `statusCode` / `code`.
   *
   * @param albumId - Album UUID.
   * @returns The assembled DSP export manifest (real SAS asset URLs).
   */
  async getAlbumDspExport(albumId: string): Promise<DspExportManifest> {
    return this.authFetch<DspExportManifest>(`/admin/albums/${albumId}/dsp-export`)
  }

  async saveIteration(data: SaveIterationRequest): Promise<TrackAsset> {
    return this.authFetch<TrackAsset>('/admin/track-assets/save-iteration', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** Save a Suno style version. Omit `albumId` for a song that is on no album. */
  async saveSunoStyle(data: SaveSunoStyleRequest): Promise<TrackAsset> {
    return this.authFetch<TrackAsset>('/admin/track-assets/save-suno-style', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async saveSunoPrompt(data: {
    trackId: string
    albumId: string
    content: string
    sourceExportId?: string | null
    sourceStyleAssetId?: string | null
    sourceLyricsAssetId?: string | null
  }): Promise<TrackAsset> {
    return this.authFetch<TrackAsset>('/admin/track-assets/save-suno-prompt', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** Save an original-lyrics version. Omit `albumId` for a song that is on no album. */
  async saveOriginalLyrics(data: SaveOriginalLyricsRequest): Promise<TrackAsset> {
    return this.authFetch<TrackAsset>('/admin/track-assets/save-original-lyrics', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async rollbackTrackAsset(id: string, reason?: string): Promise<RollbackTrackAssetResponse> {
    return this.authFetch<RollbackTrackAssetResponse>(`/admin/track-assets/${id}/rollback`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    })
  }

  // ========== Consultant API Methods ==========

  async getConsultantAlbums(): Promise<ConsultantAlbum[]> {
    const response = await this.authFetch<{ data: ConsultantAlbum[] }>('/consultant/albums')
    return response.data
  }

  async getConsultantAlbum(albumId: string): Promise<ConsultantAlbumDetail> {
    return this.authFetch<ConsultantAlbumDetail>(`/consultant/albums/${albumId}`)
  }

  async getConsultantAlbumTracks(albumId: string): Promise<ConsultantTrack[]> {
    const response = await this.authFetch<{ data: ConsultantTrack[] }>(`/consultant/albums/${albumId}/tracks`)
    return response.data
  }

  async getConsultantTrackDetail(albumId: string, trackId: string): Promise<Track> {
    return this.authFetch<Track>(`/consultant/albums/${albumId}/tracks/${trackId}`)
  }

  async getConsultantTrackAssets(albumId: string, trackId: string): Promise<TrackAsset[]> {
    const response = await this.authFetch<{ data: TrackAsset[] }>(`/consultant/albums/${albumId}/tracks/${trackId}/assets`)
    return response.data
  }

  // ========== Admin Album Access Methods ==========

  async listAlbumAccess(userId: string): Promise<ConsultantAlbumAccess[]> {
    const response = await this.authFetch<{ data: ConsultantAlbumAccess[] }>(`/admin/users/${userId}/album-access`)
    return response.data
  }

  async grantAlbumAccess(userId: string, albumId: string): Promise<ConsultantAlbumAccess> {
    return this.authFetch<ConsultantAlbumAccess>(`/admin/users/${userId}/album-access`, {
      method: 'POST',
      body: JSON.stringify({ albumId }),
    })
  }

  async revokeAlbumAccess(userId: string, albumId: string): Promise<void> {
    await this.authFetch<null>(`/admin/users/${userId}/album-access/${albumId}`, {
      method: 'DELETE',
    })
  }

  // ========== Admin Transcription Methods ==========

  async triggerTranscription(trackId: string): Promise<TranscriptionJob> {
    return this.authFetch<TranscriptionJob>(`/admin/tracks/${trackId}/transcribe`, {
      method: 'POST',
    })
  }

  async getTranscriptionStatus(trackId: string): Promise<TranscriptionJobStatusResponse> {
    return this.authFetch<TranscriptionJobStatusResponse>(`/admin/tracks/${trackId}/transcribe/status`)
  }

  // ========== Conductor Voice Methods (ADR-095) ==========

  /**
   * Transcribe a recorded directive clip via ElevenLabs Scribe (voice input).
   * The clip is sent base64-encoded with its container MIME type. CMS-only
   * server-side (`501 CMS_ONLY` in static mode), matching the mic being hidden.
   *
   * @param audioBase64 - The recorded clip, base64-encoded (no data-URL prefix).
   * @param mimeType - The clip's container MIME type (e.g. `audio/webm`).
   * @param signal - Optional abort signal to cancel the in-flight request.
   * @returns The final transcript text and coarse segments.
   */
  async transcribeVoice(audioBase64: string, mimeType: string, signal?: AbortSignal): Promise<VoiceTranscribeResponse> {
    return this.authFetch<VoiceTranscribeResponse>('/admin/voice/transcribe', {
      method: 'POST',
      body: JSON.stringify({ audioBase64, mimeType }),
      signal,
    })
  }

  /**
   * Synthesize read-aloud speech for an agent turn (ElevenLabs TTS). Returns the
   * raw `audio/mpeg` bytes (not JSON), so this bypasses the JSON `fetch` helper.
   * Available in every content mode. `voiceId` overrides the Conductor voice.
   *
   * @param text - The text to read aloud.
   * @param voiceId - Optional ElevenLabs voice id override.
   * @param signal - Optional abort signal to cancel the in-flight request.
   * @returns The synthesized audio bytes.
   */
  async synthesizeVoice(text: string, voiceId?: string, signal?: AbortSignal): Promise<ArrayBuffer> {
    if (!this.getToken) {
      throw new Error('Token provider not configured')
    }
    const token = await this.getToken()
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(`${this.baseUrl}/admin/voice/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(voiceId ? { text, voiceId } : { text }),
      signal,
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      const message = body?.error?.message ?? body?.message ?? `Request failed: ${response.status}`
      const code = body?.error?.code ?? 'UNKNOWN'
      const correlationId = body?.error?.correlationId ?? response.headers.get('x-correlation-id') ?? undefined
      const diagnosticsUrl = body?.error?.diagnosticsUrl ?? undefined
      throw new ApiError(message, response.status, code, correlationId, diagnosticsUrl)
    }

    return response.arrayBuffer()
  }

  // ========== ADR-179 Catalog and album placements (TASK-3022) ==========
  // Mirrors @tc/hub-client `catalog`, `placements` and `tracks.replaceMaster`.

  /**
   * Search the hub artist's catalog. Songs that are on no album are normal rows.
   *
   * @param params - Optional text, placement, kind, stage and tag filters, and paging.
   * @returns One page of songs plus facet counts.
   */
  async searchCatalog(params: CatalogSearchParams = {}): Promise<CatalogSearchResult> {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') query.set(key, String(value))
    }
    const qs = query.toString()
    return this.authFetch<CatalogSearchResult>(`/admin/catalog${qs ? `?${qs}` : ''}`)
  }

  /**
   * Create a song from a title alone. A taken slug throws an `ApiError` whose
   * `conflict` names the existing song.
   *
   * @param body - Title and optional slug, kind, stage and tags.
   * @returns The new song, on no album.
   */
  async createCatalogTrack(body: CreateCatalogTrackRequest): Promise<CatalogTrackSummary> {
    return this.authFetch<CatalogTrackSummary>('/admin/catalog', { method: 'POST', body: JSON.stringify(body) })
  }

  /** A song with its tags, takes, text versions, album usages and review refs. */
  async getCatalogTrack(id: string): Promise<CatalogTrackDetail> {
    return this.authFetch<CatalogTrackDetail>(`/admin/catalog/${id}`)
  }

  /** Edit a song's title, kind, stage or tags (tags replace the whole set). */
  async updateCatalogTrack(id: string, body: UpdateCatalogTrackRequest): Promise<CatalogTrackDetail> {
    return this.authFetch<CatalogTrackDetail>(`/admin/catalog/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
  }

  /** Every album the song is on, with its resolved take, lyrics and style. */
  async getCatalogUsages(id: string): Promise<TrackUsage[]> {
    return (await this.authFetch<{ data: TrackUsage[] }>(`/admin/catalog/${id}/usages`)).data
  }

  /** The song's album timeline, newest first. */
  async getCatalogEvents(id: string, limit?: number): Promise<PlacementEvent[]> {
    const qs = limit ? `?limit=${limit}` : ''
    return (await this.authFetch<{ data: PlacementEvent[] }>(`/admin/catalog/${id}/events${qs}`)).data
  }

  /** Where an album is published, per platform, latest first (SPEC-055). */
  async listAlbumPublications(albumId: string): Promise<AlbumPublication[]> {
    return (await this.authFetch<{ data: AlbumPublication[] }>(`/admin/albums/${albumId}/publications`)).data
  }

  /** Create or update one album publication (matched by id, else platform + kind + externalId). */
  async upsertAlbumPublication(albumId: string, body: UpsertAlbumPublication): Promise<AlbumPublication> {
    return this.authFetch<AlbumPublication>(`/admin/albums/${albumId}/publications`, { method: 'PUT', body: JSON.stringify(body) })
  }

  /** An album's placements in position order. */
  async listPlacements(albumId: string): Promise<AlbumPlacement[]> {
    return (await this.authFetch<{ data: AlbumPlacement[] }>(`/admin/albums/${albumId}/placements`)).data
  }

  /** Place a song on an album; position defaults to the end. */
  async addPlacement(albumId: string, body: AddPlacement): Promise<AlbumPlacement> {
    return this.authFetch<AlbumPlacement>(`/admin/albums/${albumId}/placements`, { method: 'POST', body: JSON.stringify(body) })
  }

  /** Take a song off an album. The song and its assets stay in the catalog. */
  async removePlacement(albumId: string, trackId: string): Promise<CatalogTrack> {
    return this.authFetch<CatalogTrack>(`/admin/albums/${albumId}/placements/${trackId}`, { method: 'DELETE' })
  }

  /** Reorder an album: `trackIds` must be exactly its placed songs, in the new order. */
  async reorderPlacements(albumId: string, trackIds: string[]): Promise<AlbumPlacement[]> {
    return (await this.authFetch<{ data: AlbumPlacement[] }>(`/admin/albums/${albumId}/placements/order`, {
      method: 'PUT',
      body: JSON.stringify({ trackIds }),
    })).data
  }

  /** Put a different song in an occupied slot; returns the displaced song's id. */
  async replacePlacement(albumId: string, body: ReplacePlacement): Promise<ReplacePlacementResult> {
    return this.authFetch<ReplacePlacementResult>(`/admin/albums/${albumId}/placements/replace`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /** Repin a slot's take, lyrics or style; `null` follows the latest. */
  async updatePlacementSelections(albumId: string, trackId: string, body: UpdatePlacementSelections): Promise<AlbumPlacement> {
    return this.authFetch<AlbumPlacement>(`/admin/albums/${albumId}/placements/${trackId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  }

  /** Record a new master take for the same song (ADR-122); starts a re-derive job. */
  async replaceMaster(trackId: string, body: ReplaceMasterRequest): Promise<ReplaceMasterResponse> {
    return this.authFetch<ReplaceMasterResponse>(`/admin/tracks/${trackId}/replace-master`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  /** The latest re-derive job for a song. */
  async getRederiveStatus(trackId: string): Promise<RederiveJob> {
    return this.authFetch<RederiveJob>(`/admin/tracks/${trackId}/rederive/status`)
  }

  // ========== SPEC-017 Lyrics Studio Methods ==========

  /** Re-run transcription from a (possibly different) stem without losing prior artifacts. */
  async retranscribe(trackId: string, data: RetranscribeRequest = {}): Promise<TranscriptionJob> {
    return this.authFetch<TranscriptionJob>(`/admin/tracks/${trackId}/retranscribe`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** (Re)generate AI-Structured Sung Lyrics from a raw transcript. */
  async structureLyrics(trackId: string, data: StructureLyricsRequest): Promise<StructureLyricsResponse> {
    return this.authFetch<StructureLyricsResponse>(`/admin/tracks/${trackId}/structure`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** Reconcile existing lyrics and transcript locally, without rerunning transcription. */
  async alignLyrics(trackId: string, data?: AlignLyricsRequest): Promise<AlignLyricsCreated | AlignLyricsSkipped> {
    return this.authFetch<AlignLyricsCreated | AlignLyricsSkipped>(`/admin/tracks/${trackId}/align-lyrics`, {
      method: 'POST',
      ...(data ? { body: JSON.stringify(data) } : {}),
    })
  }

  /** The three preserved lyrics artifacts + the latest take's stems. */
  async getTrackArtifacts(trackId: string): Promise<TrackArtifactsResponse> {
    return this.authFetch<TrackArtifactsResponse>(`/admin/tracks/${trackId}/artifacts`)
  }

  /** The three-way Compare & Understand payload (artifacts + segments + diff + insights). */
  async getTrackCompare(trackId: string): Promise<TrackCompareResponse> {
    return this.authFetch<TrackCompareResponse>(`/admin/tracks/${trackId}/compare`)
  }

  /** Apply a non-destructive Evolve disposition (keep/use/mark a version or take). */
  async applyDisposition(trackId: string, data: TrackDispositionRequest): Promise<TrackDispositionResponse> {
    return this.authFetch<TrackDispositionResponse>(`/admin/tracks/${trackId}/disposition`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /** Ask the AI producer for a bounded, single-call analysis of Suno's take. */
  async analyzeTrack(trackId: string, data: AnalyzeTrackRequest = {}): Promise<AnalyzeTrackResponse> {
    return this.authFetch<AnalyzeTrackResponse>(`/admin/tracks/${trackId}/analyze`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }
}

export { ApiClient }
export const api = new ApiClient(API_BASE_URL)
export type { ArtistWithTheme, ReleaseWithLinks, HealthResponse, TrackAssetUploadUrlResponse, TrackAssetDuplicateResponse, TrackAssetUploadUrlResult }
